import { ReactNode } from 'react';

import { Box, Divider, Flex, Stack, Text, useTheme } from '@chakra-ui/react';

import { getCustomFormattedDay } from 'utils';

import { AppointmentInterface } from 'types';

import { CustomScrollBar } from 'components';
import { Loader } from 'ui';
import { AppointmentList } from './components';
import {
  AppointmentsState,
  DateRange,
  groupAndFormatData
} from '../AdvancedSearch/helpers';

interface AppointmentDataProps {
  showResultContent: boolean;
  isLoading: boolean;
  appointments: AppointmentsState;
  onSelectAppointment: (appointment: AppointmentInterface) => void;
  selectedDate: DateRange | null;
  children?: ReactNode;
}

const AppointmentData = ({
  showResultContent,
  appointments,
  onSelectAppointment,
  children,
  selectedDate,
  isLoading
}: AppointmentDataProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepTime: { apptData }
        }
      }
    }
  } = useTheme();

  const appointmentsByDate = groupAndFormatData(appointments || {});
  const appointmentsArray = appointmentsByDate
    ? Object.entries(appointmentsByDate)
    : [];
  const isAppointmentsExist = appointmentsArray.length > 0;
  const isInitialSearch = appointments === undefined;
  const isDelayBeforeSet = appointments === null;
  const isDateTheSame = selectedDate?.startDate === selectedDate?.endDate;

  const renderHelperText = () =>
    isInitialSearch ? (
      <Stack maxW='280px' gap='14px' mb={{ xl: '65px' }}>
        <Text
          fontSize={{ base: '16px', md: '18px' }}
          lineHeight='180%'
          textAlign='center'
        >
          Please select First Available or a desired Appointment Date
        </Text>
      </Stack>
    ) : (
      <Stack gap='5px'>
        <Text
          fontSize={{ base: '22px', md: '24px' }}
          textAlign='center'
          color={apptData.noOpeningsTextColor}
        >
          No Openings
        </Text>
        {selectedDate && (
          <Text
            fontSize={{ base: '16px', md: '18px' }}
            lineHeight='180%'
            textAlign='center'
            color={apptData.noOpeningsTextColor}
          >
            {isDateTheSame
              ? getCustomFormattedDay(selectedDate.startDate)
              : `${getCustomFormattedDay(
                  selectedDate.startDate
                )} - ${getCustomFormattedDay(selectedDate.endDate)}`}
          </Text>
        )}
      </Stack>
    );

  const renderContent = () =>
    isAppointmentsExist ? (
      <CustomScrollBar maxHeight='585px'>
        <Stack
          gap='20px'
          padding={{ base: '15px', md: '20px' }}
          pt={{ base: showResultContent ? '0' : '15px', md: '20px' }}
          w='100%'
        >
          {appointmentsArray.map(([date, appointmentsData]) => (
            <AppointmentList
              key={date}
              title={date}
              appointments={appointmentsData}
              onSelectAppointment={onSelectAppointment}
            />
          ))}
        </Stack>
      </CustomScrollBar>
    ) : (
      renderHelperText()
    );

  return (
    <Flex
      bg={apptData.backgroundColor}
      borderRadius='6px'
      flexDirection={{ base: 'column', md: 'row' }}
    >
      <Box
        flexShrink={0}
        display={{ base: showResultContent ? 'none' : 'block', md: 'block' }}
        w={{ base: '100%', md: '310px', lg: '380px', xl: '450px' }}
      >
        {children}
      </Box>

      <Divider
        h='auto'
        w='4px'
        border='none'
        bg={apptData.dividerColor}
        orientation='vertical'
        flexShrink={0}
        my='-1px'
      />

      <Stack
        position='relative'
        w='100%'
        display={{ base: showResultContent ? 'flex' : 'none', md: 'flex' }}
        alignItems='center'
        justifyContent={isAppointmentsExist ? 'flex-start' : 'center'}
        minHeight='585px'
      >
        {isLoading || isDelayBeforeSet ? (
          <>
            <Box
              position='fixed'
              top={0}
              left={0}
              width='100vw'
              height='100vh'
              zIndex={1000}
            />
            <Loader absolute />
          </>
        ) : (
          renderContent()
        )}
      </Stack>
    </Flex>
  );
};

export default AppointmentData;
